import { ApolloProvider } from '@apollo/client';
import { getApolloCache } from '@oolio-group/client-utils';
import { GQLClient } from '@oolio-group/gql-client';
import {
  LocalizationProvider,
  supportedLocales,
} from '@oolio-group/localization';
import React, { useCallback, useState, useEffect } from 'react';
import { StatusBar } from 'react-native';
import config from './config';
import AppNavigator from './navigator/AppNavigator';
import * as settings from './state/preferences';
import { subscriptionStateUtil } from './state/subscriptionStateUtils';
import useBehaviorSubjectState from './hooks/rxjs/useSubjectState';
import { sessionSubject } from './state/sessionObservable';
import { ThemeProvider } from './hooks/useTheme';
import { refreshTokenAccessToken } from './utils/refreshTokenAccessToken';
import { tokenManager } from './utils/tokenManager';
import { localComms } from './utils/localComms';
import { initializeSecondaryDisplay } from './utils/secondaryDisplay';
import { useNetworkStatusListener } from './hooks/connectivity/useNetworkListener';
import { useAutoSyncOnReconnect } from './hooks/connectivity/useAutoSyncOnReconnect';
import {
  authFlowLink,
  isInvalidGrant,
  clearSession,
  safeStringify,
} from './graphql/authFlowLink';
import localCommsLink from './graphql/localCommsLink';
import { version as pkgVersion } from '../package.json';
import { getAppVersion } from './utils/getAppVersion';
import { App as AppType } from '@oolio-group/domain';
import Sentry from './utils/Sentry/Sentry';
import { isEmbedded } from './embedded/embedded';
import { useAuditUploadCycle } from './actionLogging/useAuditUploadCycle';
import { useCdsReactiveAudit } from './actionLogging/useCdsReactiveAudit';

const DEFAULT_COUNTRY_CODE = 'AU';
const DEFAULT_LOCALE = 'en-US';

const gqlClientData = {
  client: 'cds-app',
  version: pkgVersion,
};

const App = () => {
  useNetworkStatusListener();
  useAutoSyncOnReconnect();
  useAuditUploadCycle();
  useCdsReactiveAudit();
  const retryAwait = useCallback(async () => {
    try {
      const session = await settings.getSession();
      if (!session?.authorized) return;
      if ((session.expiredDate as number) > Date.now()) return;
      await refreshTokenAccessToken();
    } catch (error) {
      const isDead = isInvalidGrant(error);
      Sentry.captureMessage('Token refresh failed', {
        tags: {
          stage: 'App-retryAwait',
          errorType: isDead ? 'invalid_grant' : 'other',
        },
        extra: {
          error:
            error instanceof Error
              ? error.message
              : error && typeof error === 'object'
              ? safeStringify(error)
              : String(error),
        },
      });
      if (isDead) {
        await clearSession();
      }
    }
  }, []);

  useEffect(() => {
    const start = async () => {
      try {
        initializeSecondaryDisplay().catch(() => {});
        await localComms.init(
          gqlClientData.client,
          gqlClientData.version,
          config.API_URL,
        ); //should be initialized as early as possible.
        const isEnabled = (await settings.getEnableLocalComms()) || false;
        localComms.onLocalCommsToggleChange(isEnabled);
      } catch (error) {
        console.error('Error initializing localComms:', error);
      }
    };
    start();
  }, []);

  // On component mount, restore cache
  // Meanwhile app renders a loading screen
  const [client] = useState(() => {
    const cache = getApolloCache();

    const getSession = async () => {
      return await settings.getSession();
    };

    const middlewareLinks = [...authFlowLink, localCommsLink];
    return new GQLClient(
      {
        cache,
        middlewares: middlewareLinks,
        service: config.API_URL,
        getSession,
        client: gqlClientData.client,
        version: gqlClientData.version,
        retryWait: retryAwait,
      },
      true,
      {
        onChangeSubscriptionState: subscriptionStateUtil.updateState,
      },
    ).getApolloClient();
  });

  //fetch session
  const { value: session } = useBehaviorSubjectState(sessionSubject);

  // Start token manager when session is authorized
  useEffect(() => {
    if (session.authorized) {
      tokenManager.start();
    } else {
      tokenManager.stop();
    }

    return () => {
      tokenManager.stop();
    };
  }, [session.authorized]);

  useEffect(() => {
    Sentry.setTags({
      organizationName: session.currentOrganization?.name,
      organization: session.currentOrganization?.id,
      venue: session.currentVenue?.id,
      store: session.currentStore?.id,
      device: session.customerDisplay?.id ?? session.device?.id,
      app: AppType.CUSTOMER_DISPLAY_APP,
      appVersion: getAppVersion(),
      embedded: isEmbedded(),
    });
  }, [
    session.currentOrganization?.id,
    session.currentOrganization?.name,
    session.currentVenue?.id,
    session.currentStore?.id,
    session.customerDisplay?.id,
    session.device?.id,
  ]);

  return (
    <>
      <StatusBar animated={true} barStyle="light-content" hidden />
      <ApolloProvider client={client}>
        <LocalizationProvider
          locale={supportedLocales[DEFAULT_LOCALE]}
          country={session.currentOrganization?.country || DEFAULT_COUNTRY_CODE}
        >
          <ThemeProvider>
            <AppNavigator />
          </ThemeProvider>
        </LocalizationProvider>
      </ApolloProvider>
    </>
  );
};
export default App;
